import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiDownload, ApiError } from '../api/client';
import { businessBySlug } from '../domain/businesses';
import { formatMkd, formatNumber, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import { useToast } from '../components/Toast';

/**
 * Raportet — spend per article, grouped by category, with subtotals.
 * Ports frmShpenzimetRaportet.
 */

interface ReportArticle {
  artikulli: string;
  quantity: number | null;
  total: number;
}

interface ReportCategory {
  categoryId: number;
  kategoria: string;
  articles: ReportArticle[];
  subtotalQuantity: number;
  subtotal: number;
}

interface Report {
  categories: ReportCategory[];
  grandTotalQuantity: number;
  grandTotal: number;
}

export default function ShpenzimetRaportet() {
  const { biznesi = '' } = useParams();
  const business = businessBySlug(biznesi);
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);

  const reportQuery = useQuery({
    queryKey: ['raportet', biznesi, search, startDate, endDate],
    queryFn: () =>
      apiGet<Report>(`/shpenzimet/raportet/${biznesi}`, { search, startDate, endDate }),
    enabled: Boolean(business),
    placeholderData: (prev) => prev,
  });

  async function handleExport() {
    try {
      await apiDownload(`/shpenzimet/raportet/${biznesi}/eksport`, {
        search,
        startDate,
        endDate,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.');
    }
  }

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  const report = reportQuery.data;
  const categories = report?.categories ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Raporti i shpenzimeve — {business.name}</h1>
        <p className="text-sm text-muted">Shpenzimet për artikull, të grupuara sipas kategorisë</p>
      </div>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko kategori ose artikull"
      >
        <button
          type="button"
          onClick={handleExport}
          disabled={categories.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
      </FilterBar>

      {reportQuery.isLoading && (
        <div className="rounded-xl bg-white p-10 text-center text-muted">Duke ngarkuar…</div>
      )}

      {!reportQuery.isLoading && categories.length === 0 && (
        <div className="rounded-xl bg-white p-10 text-center text-muted">
          Nuk ka të dhëna për këtë periudhë.
        </div>
      )}

      {categories.length > 0 && (
        <div className="overflow-hidden rounded-xl bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 font-semibold">Nënkategoria</th>
                  <th className="px-4 py-3 font-semibold">Artikulli</th>
                  <th className="px-4 py-3 text-right font-semibold">Sasia</th>
                  <th className="px-4 py-3 text-right font-semibold">Totali</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <ReportCategoryRows key={c.categoryId} category={c} />
                ))}

                <tr className="border-t-2 border-slate-400 bg-slate-700 font-semibold text-white">
                  <td className="px-4 py-3" colSpan={2}>
                    TOTAL
                  </td>
                  <td className="tnum px-4 py-3 text-right">
                    {formatNumber(report?.grandTotalQuantity ?? 0)}
                  </td>
                  <td className="tnum px-4 py-3 text-right">{formatMkd(report?.grandTotal ?? 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCategoryRows({ category }: { category: ReportCategory }) {
  return (
    <>
      {category.articles.map((a, i) => (
        <tr key={`${category.categoryId}-${a.artikulli}`} className="border-b border-slate-100">
          {/* Category name printed once per group rather than repeated on every row. */}
          <td className="px-4 py-2 text-muted">{i === 0 ? category.kategoria : ''}</td>
          <td className="px-4 py-2">{a.artikulli}</td>
          <td className="tnum px-4 py-2 text-right">
            {a.quantity == null ? '' : formatNumber(a.quantity)}
          </td>
          <td className="tnum px-4 py-2 text-right">{formatMkd(a.total)}</td>
        </tr>
      ))}

      <tr className="border-b border-slate-200 bg-brand-50 font-semibold text-brand-700">
        <td className="px-4 py-2">{category.kategoria}</td>
        <td className="px-4 py-2">&gt;&gt; Nëntotali</td>
        <td className="tnum px-4 py-2 text-right">{formatNumber(category.subtotalQuantity)}</td>
        <td className="tnum px-4 py-2 text-right">{formatMkd(category.subtotal)}</td>
      </tr>
    </>
  );
}
